import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faUsers, faFileContract, faShieldHalved, faChartLine } from '@fortawesome/free-solid-svg-icons';
import { faGoogle, faMicrosoft, faSlack, faAws } from '@fortawesome/free-brands-svg-icons';
import { useCountUp } from '../../hooks/useCountUp';
import { useInView } from '../../hooks/useInView';
import { GlassCard } from './GlassCard';
import { FadeUp } from './FadeUp';

const STATS = [
  { icon: faUsers, value: 2847, suffix: '+', label: '활성 사용자', color: 'var(--color-accent)' },
  { icon: faFileContract, value: 15200, suffix: '+', label: '성사된 딜', color: 'var(--color-success)' },
  { icon: faShieldHalved, value: 99.9, suffix: '%', label: '가동률', color: 'var(--color-info)' },
  { icon: faChartLine, value: 42, suffix: '%', label: '빠른 클로징', color: 'var(--color-warning)' },
];

const PARTNERS = [
  { icon: faGoogle, name: 'Google' },
  { icon: faMicrosoft, name: 'Microsoft' },
  { icon: faSlack, name: 'Slack' },
  { icon: faAws, name: 'AWS' },
];

function StatCard({ icon, value, suffix, label, color, enabled }: typeof STATS[0] & { enabled: boolean }) {
  const count = useCountUp(Math.floor(value), 2000, 0, enabled);
  const isDecimal = value % 1 !== 0;

  return (
    <GlassCard>
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 'var(--space-3)', textAlign: 'center' }}>
        <div
          style={{
            width: '3rem',
            height: '3rem',
            borderRadius: 'var(--radius-lg)',
            background: `color-mix(in oklch, ${color} 15%, transparent)`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <FontAwesomeIcon icon={icon} style={{ color, fontSize: 'var(--text-xl)' }} />
        </div>
        <div style={{ fontFamily: 'var(--font-display)', fontSize: 'var(--text-3xl)', fontWeight: 800, color: 'var(--color-text-primary)' }}>
          {isDecimal ? value : count}{suffix}
        </div>
        <div style={{ fontSize: 'var(--text-sm)', color: 'var(--color-text-tertiary)', fontWeight: 500 }}>
          {label}
        </div>
      </div>
    </GlassCard>
  );
}

export function SocialProof() {
  const { ref, isInView } = useInView(0.2);

  return (
    <section ref={ref} aria-labelledby="social-proof-heading" style={{ padding: 'var(--space-16) 0' }}>
      <FadeUp>
        <h2
          id="social-proof-heading"
          style={{ textAlign: 'center', marginBottom: 'var(--space-4)', fontSize: 'var(--text-3xl)' }}
        >
          업계 리더들이 신뢰합니다
        </h2>
        <p style={{ textAlign: 'center', marginBottom: 'var(--space-12)', color: 'var(--color-text-secondary)' }}>
          수천 개 영업팀이 딜 사이클을 가속하고 있습니다
        </p>
      </FadeUp>

      {/* Stats Grid - Bento */}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(2, 1fr)',
          gap: 'var(--space-4)',
          marginBottom: 'var(--space-12)',
        }}
        className="stats-grid"
      >
        {STATS.map((stat, i) => (
          <FadeUp key={stat.label} delay={i * 0.1}>
            <StatCard {...stat} enabled={isInView} />
          </FadeUp>
        ))}
      </div>

      {/* Partner Logos */}
      <FadeUp delay={0.4}>
        <div style={{ textAlign: 'center', marginBottom: 'var(--space-6)' }}>
          <span
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 'var(--space-2)',
              padding: 'var(--space-2) var(--space-4)',
              borderRadius: 'var(--radius-full)',
              background: 'var(--color-accent-subtle)',
              fontSize: 'var(--text-xs)',
              fontWeight: 600,
              color: 'var(--color-accent)',
              textTransform: 'uppercase',
              letterSpacing: '0.05em',
            }}
          >
            <FontAwesomeIcon icon={faShieldHalved} /> SOC 2 인증
          </span>
        </div>
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 'var(--space-10)',
            flexWrap: 'wrap',
            opacity: 0.5,
          }}
        >
          {PARTNERS.map(partner => (
            <div
              key={partner.name}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 'var(--space-2)',
                color: 'var(--color-text-tertiary)',
                fontSize: 'var(--text-2xl)',
              }}
              title={partner.name}
            >
              <FontAwesomeIcon icon={partner.icon} />
              <span style={{ fontSize: 'var(--text-sm)', fontWeight: 600 }}>{partner.name}</span>
            </div>
          ))}
        </div>
      </FadeUp>

      <style>{`
        @media (min-width: 768px) {
          .stats-grid {
            grid-template-columns: repeat(4, 1fr) !important;
          }
        }
      `}</style>
    </section>
  );
}

export function LiveUsersBadge() {
  const count = useCountUp(127, 3000, 89, true);
  return (
    <div
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 'var(--space-2)',
        padding: 'var(--space-2) var(--space-4)',
        borderRadius: 'var(--radius-full)',
        background: 'var(--color-surface-glass)',
        backdropFilter: 'blur(12px)',
        border: '1px solid var(--color-surface-glass-border)',
        fontSize: 'var(--text-sm)',
        color: 'var(--color-text-secondary)',
      }}
    >
      <span
        style={{
          width: '8px',
          height: '8px',
          borderRadius: '50%',
          background: 'var(--color-success)',
          boxShadow: '0 0 8px var(--color-success)',
          animation: 'pulse 2s infinite',
        }}
      />
      <span style={{ fontWeight: 600 }}>{count}</span>명 접속 중
      <style>{`
        @keyframes pulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.5; }
        }
      `}</style>
    </div>
  );
}
